import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';

interface InboxItem {
  id: string;
  status: string;
  slaStatus: 'offen' | 'erfuellt' | 'verletzt' | null;
  slaReaktionBis: string | null;
}

export const Route = createFileRoute('/')({
  beforeLoad: async () => {
    try {
      await api('/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: '/login' });
      }
      throw err;
    }
  },
  component: Dashboard,
});

function Dashboard() {
  const session = useSession();

  // Inbox-Widget: laedt offene Anfragen + zaehlt nach Bucket
  const inboxQ = useQuery({
    queryKey: ['inbox', 'dashboard'],
    queryFn: () => api<{ items: InboxItem[] }>('/api/v1/inbox?limit=200'),
    refetchInterval: 60_000, // einmal pro Minute aktualisieren
  });

  const items = inboxQ.data?.items ?? [];
  const counts = items.reduce(
    (acc, a) => {
      if (a.status === 'neu') acc.neu += 1;
      if (a.status === 'in_triage') acc.inTriage += 1;
      if (a.status === 'zugeordnet') acc.zugeordnet += 1;
      // Verletzt zaehlen wir entweder via DB-Status oder via Berechnung
      // (offen + jetzt > Frist).
      const istVerletzt =
        a.slaStatus === 'verletzt' ||
        (a.slaStatus === 'offen' &&
          a.slaReaktionBis !== null &&
          new Date(a.slaReaktionBis).getTime() < Date.now());
      if (istVerletzt && a.status !== 'konvertiert' && a.status !== 'geschlossen') {
        acc.slaVerletzt += 1;
      }
      return acc;
    },
    { neu: 0, inTriage: 0, zugeordnet: 0, slaVerletzt: 0 },
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          Willkommen{session.data ? `, ${session.data.email}` : ''}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          busch-erp Phase 4 — Inbox aktiv. Wähle einen Bereich aus der Navigation.
        </p>
      </div>

      <InboxWidget counts={counts} loading={inboxQ.isLoading} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="Inbox" to="/inbox" description="Neue Anfragen, Triage, SLA-Status" />
        <Card title="Kunden" to="/kunden" description="Firmen, USt-ID, Kundennummern" />
        <Card
          title="Angebote"
          to="/angebote"
          description="Angebote mit Positionen, Status-Flow und Nummernkreis"
        />
        <Card
          title="Rechnungen"
          to="/rechnungen"
          description="Rechnungen, Abschläge, Schluss, Gutschrift, Storno"
        />
        <Card title="Objekte" to="/objekte" description="Gebäude/Liegenschaften pro Kunde" />
        <Card
          title="Kontakte"
          to="/kontakte"
          description="Ansprechpartner, Hauptkontakt pro Kunde"
        />
        <Card
          title="Artikel"
          to="/artikel"
          description="Wiederverwendbare Positionen für Angebote/Rechnungen"
        />
        <Card
          title="Mitarbeiter"
          to="/mitarbeiter"
          description="HR-Stammdaten, Eintritt/Austritt, Lohnklasse"
        />
        <Card
          title="Benutzer & Rollen"
          to="/users"
          description="Rollen vergeben, Anzeigenamen ändern"
        />
      </div>
    </div>
  );
}

function InboxWidget({
  counts,
  loading,
}: {
  counts: { neu: number; inTriage: number; zugeordnet: number; slaVerletzt: number };
  loading: boolean;
}) {
  const total = counts.neu + counts.inTriage + counts.zugeordnet;
  return (
    <Link
      to="/inbox"
      className="block rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-brand-500 hover:shadow transition"
    >
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-slate-900">Inbox-Status</h2>
        <span className="text-xs text-slate-500">{loading ? 'lädt …' : 'live'}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Tile label="Neu" value={counts.neu} color="text-amber-700 bg-amber-50 border-amber-200" />
        <Tile
          label="In Triage"
          value={counts.inTriage}
          color="text-blue-700 bg-blue-50 border-blue-200"
        />
        <Tile
          label="Zugeordnet"
          value={counts.zugeordnet}
          color="text-indigo-700 bg-indigo-50 border-indigo-200"
        />
        <Tile
          label="SLA verletzt"
          value={counts.slaVerletzt}
          color={
            counts.slaVerletzt > 0
              ? 'text-red-700 bg-red-50 border-red-200 ring-2 ring-red-200'
              : 'text-slate-600 bg-slate-50 border-slate-200'
          }
        />
      </div>
      {total === 0 && counts.slaVerletzt === 0 ? (
        <p className="mt-3 text-xs text-slate-500">Aktuell sind alle Anfragen abgearbeitet. ✓</p>
      ) : null}
    </Link>
  );
}

function Tile({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className={`rounded-md border px-3 py-2 ${color}`}>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}

type CardTarget =
  | '/users'
  | '/kunden'
  | '/objekte'
  | '/kontakte'
  | '/artikel'
  | '/mitarbeiter'
  | '/angebote'
  | '/rechnungen'
  | '/inbox';

function Card(props: { title: string; to: CardTarget; description: string }) {
  return (
    <Link
      to={props.to}
      className="block rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-brand-500 hover:shadow transition"
    >
      <div className="font-medium text-slate-900">{props.title}</div>
      <div className="mt-1 text-sm text-slate-600">{props.description}</div>
    </Link>
  );
}
